import { bindActionCreators, Dispatch } from 'redux';
import { Button, Col, P, PortableText, Row, Section, Wrapper } from '@ahmdigital/ui';
import { connect } from 'react-redux';
import { get, includes } from 'lodash/fp';
import { LIVE_CHAT_STATUS } from '@ahmdigital/constants';
// @ts-expect-error - Automatic, Please fix when editing this file
import liveChatActions from '@ahmdigital/livechat/lib/actions';
import timezonePlaceholderSerializer from '@ahmdigital/logic/lib/utils/sanity-timezone-placeholder-serializer';

import { GET_CHAT_ONLINE_CONTACT } from '../../src/client/pages/msg/queries/query.gql';
import { getPreviewOptionsFromCookieHeader } from '../../src/client/cms/utils/preview-cookie';
import { RootState } from '../../src/client/reducers';
import { StyledHeading, StyledImage } from './styles';
import getAppleBusinessChatData from '../../src/client/utils/get-apple-business-chat-data';
import getCmsData from '../../src/client/cms/utils/get-cms-data';
import Head from '../../src/utils/head';
import liveChatConfig from '../../src/client/utils/livechat';
import LiveChatStatus from '../../src/client/components/live-chat/live-chat-status';
import messageUsPinkyPromiseImage from './images/message-us-pinky-promise.svg';
import selectIsAbcSupportedAndAvailable from '../../src/client/selectors/select-is-abc-supported';
import useGetAllPlaceholders from '../../src/client/components/offers/logic/use-get-all-placeholders';
import type { PortableTextType } from '../../src/client/types/sanityInternal';

type LiveChatProps = {
  actionsLiveChat: { launch: (chatConfig: any) => void };
  contactTitle: string;
  contactTime: PortableTextType[];
  isAbcSupportedAndAvailable: boolean;
  liveChatStatus: (typeof LIVE_CHAT_STATUS)[keyof typeof LIVE_CHAT_STATUS];
};

const MessageUsPage = ({
  contactTitle,
  contactTime,
  actionsLiveChat,
  isAbcSupportedAndAvailable,
  liveChatStatus,
}: LiveChatProps) => {
  const { placeholders } = useGetAllPlaceholders();

  const abcData = getAppleBusinessChatData({ isAbcSupportedAndAvailable });

  const isOnline = includes(liveChatStatus, [LIVE_CHAT_STATUS.BUSY, LIVE_CHAT_STATUS.ONLINE]);

  const buttonProps = isAbcSupportedAndAvailable
    ? { text: isOnline ? 'Message us' : 'Leave a message', to: get('linkTo', abcData) }
    : { onClick: () => actionsLiveChat.launch(liveChatConfig), text: 'Message us' };

  return (
    <>
      <Head>
        <title>{contactTitle}</title>
        <meta name="description" content="A page to directly chat with ahm" />
        <meta key="robots" name="robots" content="noindex, nofollow" />
      </Head>
      <Section>
        <Wrapper>
          <StyledHeading looksLike="h4" variant="marginBottomSpacer10">
            {contactTitle}
          </StyledHeading>
          <P variant="marginBottomSpacer12">
            <PortableText
              renderInline
              source={contactTime}
              typeSerializers={{
                // @ts-expect-error - Placeholder type from generated codegen has `_id?: string | null` which is too strict for @ahmdigital/logic
                placeholder: timezonePlaceholderSerializer({ placeholders }),
              }}
            />
          </P>
          <LiveChatStatus isOnline={isOnline} />
          <Row xsHorizontalAlign="center">
            <Col>
              <StyledImage src={messageUsPinkyPromiseImage.src} role="presentation" />
            </Col>
          </Row>
          {(isOnline || isAbcSupportedAndAvailable) && <Button {...buttonProps}>{buttonProps.text}</Button>}
        </Wrapper>
      </Section>
    </>
  );
};

export const getServerSideProps = async ({ req }: { req: { headers: { cookie?: string } } }) => {
  const { dataset, mode } = getPreviewOptionsFromCookieHeader(req.headers.cookie) ?? {};
  const contactTime = await getCmsData(GET_CHAT_ONLINE_CONTACT, undefined, dataset, mode);

  return {
    props: {
      contactTime: contactTime?.allContact?.[0]?.descriptionRaw,
      contactTitle: contactTime?.allContact?.[0]?.title,
    },
  };
};

const mapStateToProps = (state: RootState) => ({
  isAbcSupportedAndAvailable: selectIsAbcSupportedAndAvailable(state),
  liveChatStatus: get('livechat.status', state),
});

type DispatchProps = {
  actionsLiveChat: { launch: (chatConfig: any) => void };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  actionsLiveChat: bindActionCreators(liveChatActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(MessageUsPage);

export { MessageUsPage as MessageUsPageTest, mapStateToProps, mapDispatchToProps };
